import React, { Component } from 'react';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { FormattedMessage } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { isUserAuthorized } from '../../util/userHelpers';
import { pathByRouteName } from '../../util/routes';

import { Modal, UserDisplayName } from '../../components';
import {
  getProcess,
  resolveLatestProcessName,
  TX_TRANSITION_ACTOR_CUSTOMER,
  TX_TRANSITION_ACTOR_PROVIDER,
} from '../../transactions/transaction';

import ReviewModal from '../../containers/TransactionPage/ReviewModal/ReviewModal';
import EmailReminder from './EmailReminder';

import css from './ModalMissingInformation.module.css';

const MISSING_INFORMATION_MODAL_WHITELIST = [
  'LoginPage',
  'SignupPage',
  'ContactDetailsPage',
  'EmailVerificationPage',
  'PasswordResetPage',
  'StripePayoutPage',
];

const EMAIL_VERIFICATION = 'EMAIL_VERIFICATION';
const REVIEW_PENDING = 'REVIEW_PENDING';
class ModalMissingInformation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showMissingInformationReminder: null,
      hasSeenMissingInformationReminder: false,
    };
    this.handleMissingInformationReminder = this.handleMissingInformationReminder.bind(this);
    this.onSubmitReview = this.onSubmitReview.bind(this);
  }

  componentDidUpdate() {
    const { currentUser, currentUserHasListings, currentUserHasOrders, location } = this.props;
    const user = ensureCurrentUser(currentUser);
    this.handleMissingInformationReminder(
      user,
      currentUserHasListings,
      currentUserHasOrders,
      location
    );
  }

  handleMissingInformationReminder(
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    newLocation
  ) {
    const routes = this.props.routeConfiguration;
    const reviewTransactions = this.props.reviewTransactions;
    const whitelistedPaths = MISSING_INFORMATION_MODAL_WHITELIST.map(page =>
      pathByRouteName(page, routes)
    );

    // Is the current page whitelisted?
    const isPageWhitelisted = whitelistedPaths.includes(newLocation.pathname);

    // Track if path changes inside Page level component
    const pathChanged = newLocation.pathname !== this.props.location.pathname;
    const notRemindedYet =
      !this.state.showMissingInformationReminder && !this.state.hasSeenMissingInformationReminder;

    // Is the reminder already shown on current page
    const showOnPathChange = notRemindedYet || pathChanged;

    if (!isPageWhitelisted && showOnPathChange) {
      // Emails are sent when order is initiated
      // Customer is likely to get email soon when she books something
      // Provider email should work - she should get an email when someone books a listing
      const hasOrders = currentUserHasOrders === true;
      const hasListingsOrOrders = currentUserHasListings || hasOrders;

      const emailUnverified = !!currentUser.id && !currentUser.attributes.emailVerified;
      const emailVerificationNeeded = hasListingsOrOrders && emailUnverified;

      // Show reminder
      if (emailVerificationNeeded) {
        this.setState({ showMissingInformationReminder: EMAIL_VERIFICATION });
      } else if (reviewTransactions && reviewTransactions.length) {
        this.setState({ showMissingInformationReminder: REVIEW_PENDING });
      }
    }
  }

  // Submit review and close the review modal
  onSubmitReview = (values,) => {
    const { reviewRating, reviewContent } = values;
    const rating = Number.parseInt(reviewRating, 10);
    const params = { reviewRating: rating, reviewContent };

    const {
      config,
      currentUser,
      onSendReview,
      reviewTransactions = []
    } = this.props;

    const transaction = reviewTransactions && reviewTransactions.length
      ? reviewTransactions[0]
      : null;
    const processName = transaction && transaction.id
      ? resolveLatestProcessName(transaction.attributes?.processName)
      : null

    const process = processName ? getProcess(processName) : null;
    const { states, transitions } = process || {};

    // Determine if the current user is the provider or the customer
    const isProvider = currentUser.id.uuid === transaction.provider.id.uuid;
    const transactionRole = isProvider
      ? TX_TRANSITION_ACTOR_PROVIDER
      : TX_TRANSITION_ACTOR_CUSTOMER;
    const transitionOptions =
      transactionRole == TX_TRANSITION_ACTOR_CUSTOMER
        ? {
          transactionRole,
          reviewAsFirst: transitions?.REVIEW_1_BY_CUSTOMER,
          reviewAsSecond: transitions?.REVIEW_2_BY_CUSTOMER,
          hasOtherPartyReviewedFirst: process
            ?.getTransitionsToStates([states.REVIEWED_BY_PROVIDER])
            ?.includes(transaction.attributes.lastTransition),
        }
        : {
          transactionRole,
          reviewAsFirst: transitions?.REVIEW_1_BY_PROVIDER,
          reviewAsSecond: transitions?.REVIEW_2_BY_PROVIDER,
          hasOtherPartyReviewedFirst: process
            ?.getTransitionsToStates([states.REVIEWED_BY_CUSTOMER])
            ?.includes(transaction.attributes.lastTransition),
        };

    onSendReview(transaction, transitionOptions, params, config)
      .then(() => {
        this.setState({
          showMissingInformationReminder: null,
          hasSeenMissingInformationReminder: true,
        });
      });
  };

  render() {
    const {
      intl,
      rootClassName,
      className,
      containerClassName,
      currentUser,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      onManageDisableScrolling,
      onResendVerificationEmail,
      config,
      reviewTransactions,
      sendReviewInProgress,
      sendReviewError,
    } = this.props;

    const user = ensureCurrentUser(currentUser);
    const classes = classNames(rootClassName || css.root, className);

    let content = null;

    const currentUserLoaded = user && user.id;
    if (currentUserLoaded && isUserAuthorized(currentUser)) {
      const { customer, provider } = reviewTransactions && reviewTransactions.length
        ? reviewTransactions[0]
        : {};
      const isProvider = provider && provider.id
        ? user.id.uuid === provider.id.uuid
        : false;
      const otherPartyReviewer = isProvider ? customer : provider;
      const otherUserDisplayName = <UserDisplayName user={otherPartyReviewer} intl={intl} />;

      if (this.state.showMissingInformationReminder === EMAIL_VERIFICATION) {
        content = (
          <EmailReminder
            className={classes}
            user={user}
            onResendVerificationEmail={onResendVerificationEmail}
            sendVerificationEmailInProgress={sendVerificationEmailInProgress}
            sendVerificationEmailError={sendVerificationEmailError}
          />
        );
      } else if (this.state.showMissingInformationReminder === REVIEW_PENDING) {
        content = (
          <ReviewModal
            id="MissingInformationReview"
            isOpen={!!this.state.showMissingInformationReminder}
            isClose={true}
            onCloseModal={() => {
              this.setState({
                showMissingInformationReminder: null,
                hasSeenMissingInformationReminder: true,
              });
            }}
            onManageDisableScrolling={onManageDisableScrolling}
            onSubmitReview={this.onSubmitReview}
            reviewSent={false}
            revieweeName={otherUserDisplayName}
            sendReviewError={sendReviewError}
            sendReviewInProgress={sendReviewInProgress}
            marketplaceName={config.marketplaceName}
            otherPartyReviewer={otherPartyReviewer}
          />
        );
      }
    }

    const closeButtonMessage = (
      <FormattedMessage id="ModalMissingInformation.closeVerifyEmailReminder" />
    );

    return this.state.showMissingInformationReminder === REVIEW_PENDING ? (
      content
    ) : (
      <Modal
        id="MissingInformationReminder"
        containerClassName={containerClassName}
        isOpen={!!this.state.showMissingInformationReminder}
        onClose={() => {
          this.setState({
            showMissingInformationReminder: null,
            hasSeenMissingInformationReminder: true,
          });
        }}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        closeButtonMessage={closeButtonMessage}
      >
        {content}
      </Modal>
    );
  }
}

/**
 * Modal that tells user that they have not saved all the information the service needs.
 * This is used to remind user that they need to verify their email address.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.containerClassName overwrite components own css.container
 * @param {string} props.id
 * @param {Object} props.currentUser API entity
 * @param {Function} props.onManageDisableScrolling
 * @param {Object?} props.sendVerificationEmailError
 * @param {boolean} props.sendVerificationEmailInProgress
 * @returns {JSX.Element} Modal element if user needs to be reminded
 */
const EnhancedModalMissingInformation = props => {
  const routeConfiguration = useRouteConfiguration();

  return <ModalMissingInformation routeConfiguration={routeConfiguration} {...props} />;
};

export default EnhancedModalMissingInformation;
